
import React, {
    useMemo,
    useState,
} from 'react'

import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
} from 'recharts'

import PulseLoader from 'react-spinners/PulseLoader'

import {
    useGetEvalCategoriesQuery,
} from './evalCategorysApiSlice'

import {
    useGetEvalDimensionsQuery,
} from './evalDimensionsApiSlice'

import {
    useGetCoachUserSessionEvalMatrixsQuery,
} from './coachUserSessionEvalMatrixsApiSlice'

import {
    buildCoachUserSessionEvalTrends,
} from './coachUserSessionEvalTrendUtils'


/*
|--------------------------------------------------------------------------
| 01 - Date And Score Helpers
|--------------------------------------------------------------------------
|
| Evaluations are ordered by evaluatedAt.
|
| Dates are displayed in the coach's browser timezone.
|
|--------------------------------------------------------------------------
*/

const formatEvaluationDate = value => {

    if (!value) return '—'

    const date = new Date(value)

    if (Number.isNaN(date.getTime())) {
        return '—'
    }

    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })

}


const formatShortEvaluationDate = value => {

    if (!value) return '—'

    const date = new Date(value)

    if (Number.isNaN(date.getTime())) {
        return '—'
    }

    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
    })

}


const formatEvaluationDateTime = value => {

    if (!value) return '—'

    const date = new Date(value)

    if (Number.isNaN(date.getTime())) {
        return '—'
    }

    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })

}


const formatScore = value => {

    if (
        typeof value !== 'number' ||
        !Number.isFinite(value)
    ) {
        return '—'
    }

    return value
        .toFixed(2)
        .replace(/\.?0+$/, '')

}


const formatMovement = movement => {

    if (
        movement === null ||
        movement === undefined
    ) {
        return '—'
    }

    if (movement > 0) {
        return `+${formatScore(movement)}`
    }

    return formatScore(movement)

}


const getMovementColor = movement => {

    if (
        movement === null ||
        movement === undefined
    ) {
        return 'text-slate-500'
    }

    if (movement > 0) {
        return 'text-lime-700'
    }

    if (movement < 0) {
        return 'text-rose-600'
    }

    return 'text-slate-600'

}


/*
|--------------------------------------------------------------------------
| 02 - Stats Color Palette
|--------------------------------------------------------------------------
|
| Soft backgrounds with meaningful visual distinction.
|
| All Tailwind classes are written explicitly so that
| Tailwind can detect and retain them during compilation.
|
|--------------------------------------------------------------------------
*/

const metricColorStyles = {

    orange: {
        card: 'border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50',
        label: 'text-orange-700',
        value: 'text-orange-700',
        ring: '#ea580c',
        track: '#fed7aa',
    },

    lime: {
        card: 'border-lime-200 bg-gradient-to-br from-lime-50 to-green-50',
        label: 'text-lime-700',
        value: 'text-lime-700',
        ring: '#65a30d',
        track: '#d9f99d',
    },

    amber: {
        card: 'border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50',
        label: 'text-amber-700',
        value: 'text-amber-700',
        ring: '#d97706',
        track: '#fde68a',
    },

    purple: {
        card: 'border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50',
        label: 'text-violet-700',
        value: 'text-violet-700',
        ring: '#7c3aed',
        track: '#ddd6fe',
    },

    sky: {
        card: 'border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50',
        label: 'text-sky-700',
        value: 'text-sky-700',
        ring: '#0284c7',
        track: '#bae6fd',
    },

    teal: {
        card: 'border-teal-200 bg-gradient-to-br from-teal-50 to-emerald-50',
        label: 'text-teal-700',
        value: 'text-teal-700',
        ring: '#0d9488',
        track: '#99f6e4',
    },

    rose: {
        card: 'border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50',
        label: 'text-rose-700',
        value: 'text-rose-700',
        ring: '#e11d48',
        track: '#fecdd3',
    },

    slate: {
        card: 'border-slate-200 bg-gradient-to-br from-slate-50 to-gray-50',
        label: 'text-slate-700',
        value: 'text-slate-700',
        ring: '#64748b',
        track: '#e2e8f0',
    },

}


const categoryColorNames = [
    'purple',
    'sky',
    'lime',
    'amber',
    'teal',
    'orange',
]


/*
|--------------------------------------------------------------------------
| 03 - Circular Evaluation Score Gauge
|--------------------------------------------------------------------------
|
| Reusable score indicator:
|
| - Actual score on a scale of 1 to 5
| - Circular ring shows score / 5
| - Center shows the numeric score
| - Supports compact and standard sizes
|
| A missing score is not represented as zero.
|
|--------------------------------------------------------------------------
*/

const EvalCircularScore = ({
    score,
    size = 76,
    color = 'orange',
    strokeWidth = 7,
    showMax = true,
}) => {

    const palette =
        metricColorStyles[color] ||
        metricColorStyles.orange

    const isValid =
        typeof score === 'number' &&
        Number.isFinite(score) &&
        score >= 1 &&
        score <= 5

    const radius = (100 - strokeWidth * 2) / 2

    const circumference =
        2 * Math.PI * radius

    const percentage = isValid
        ? score / 5
        : 0

    const dashOffset =
        circumference * (1 - percentage)

    return (

        <div
            className="relative inline-flex shrink-0 items-center justify-center"
            style={{
                width: size,
                height: size,
            }}
            title={
                isValid
                    ? `Score: ${formatScore(score)} out of 5`
                    : 'Not evaluated'
            }
            role="img"
            aria-label={
                isValid
                    ? `Evaluation score ${formatScore(score)} out of 5`
                    : 'No evaluation score available'
            }
        >

            <svg
                viewBox="0 0 100 100"
                width={size}
                height={size}
                aria-hidden="true"
            >

                {/* Background Ring */}

                <circle
                    cx="50"
                    cy="50"
                    r={radius}
                    fill="none"
                    stroke={palette.track}
                    strokeWidth={strokeWidth}
                />

                {/* Score Ring */}

                {isValid && (

                    <circle
                        cx="50"
                        cy="50"
                        r={radius}
                        fill="none"
                        stroke={palette.ring}
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={dashOffset}
                        transform="rotate(-90 50 50)"
                    />

                )}

            </svg>

            {/* Center Score */}

            <div className="absolute inset-0 flex flex-col items-center justify-center">

                <span
                    className={`font-bold leading-none ${palette.value}`}
                    style={{
                        fontSize:
                            size <= 44
                                ? 12
                                : size <= 65
                                    ? 16
                                    : 22,
                    }}
                >
                    {formatScore(score)}
                </span>

                {showMax && isValid && (

                    <span
                        className="mt-0.5 text-slate-400"
                        style={{
                            fontSize:
                                size <= 50
                                    ? 8
                                    : 10,
                        }}
                    >
                        / 5
                    </span>

                )}

            </div>

        </div>

    )

}


/*
|--------------------------------------------------------------------------
| 04 - Evaluation Score Scale
|--------------------------------------------------------------------------
|
| Updated - 28 Oct 2026
|
| Replaces the gray score line with a soft graduated
| color track.
|
| Lower scores start in warm amber.
| Higher scores move towards teal and lime.
|
| Latest scores use an orange dot.
| Historical scores use a teal dot.
|
|--------------------------------------------------------------------------
*/

const EvalScoreScale = ({
    score,
    compact = false,
    highlight = false,
    showValue = true,
}) => {

    const isValid =
        typeof score === 'number' &&
        Number.isFinite(score) &&
        score >= 1 &&
        score <= 5

    if (!isValid) {

        return (

            <div
                className="flex min-h-8 items-center justify-center text-xs text-slate-300"
                title="Not evaluated"
            >
                —
            </div>

        )

    }

    const position =
        ((score - 1) / 4) * 100

    return (

        <div
            className={`flex items-center gap-2 ${
                compact
                    ? 'min-w-[90px]'
                    : 'min-w-[120px]'
            }`}
            title={`Evaluation score: ${formatScore(score)} out of 5`}
        >

            <div className="relative flex-1 px-[6px]">

                <div className="relative h-[20px]">

                    {/* Colored Evaluation Scale */}

                    <div
                        className="absolute inset-x-0 top-1/2 h-[5px] -translate-y-1/2 rounded-full shadow-sm"
                        style={{
                            background:
                                'linear-gradient(to right, #fda4af 0%, #fcd34d 25%, #bef264 55%, #5eead4 100%)',
                        }}
                    />

                    {/* Scale Dot */}

                    <div
                        className={`absolute top-1/2 h-[12px] w-[12px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-md ${
                            highlight
                                ? 'bg-orange-600 ring-2 ring-orange-200'
                                : 'bg-teal-700 ring-1 ring-teal-200'
                        }`}
                        style={{
                            left: `calc(6px + (100% - 12px) * ${position / 100})`,
                        }}
                    />

                </div>

            </div>

            {showValue && (

                <span
                    className={`w-6 shrink-0 text-right text-[10px] font-bold ${
                        highlight
                            ? 'text-orange-600'
                            : 'text-teal-700'
                    }`}
                >
                    {formatScore(score)}
                </span>

            )}

        </div>

    )

}


/*
|--------------------------------------------------------------------------
| 05 - Colored Stats Metric Card
|--------------------------------------------------------------------------
|
| Supports:
|
| - Numeric metrics
| - Circular evaluation score
| - Different soft background palettes
|
|--------------------------------------------------------------------------
*/

const EvalMetricCard = ({
    label,
    value,
    detail,
    color = 'slate',
    score = null,
    showGauge = false,
}) => {

    const palette =
        metricColorStyles[color] ||
        metricColorStyles.slate

    return (

        <div
            className={`rounded-lg border p-3 shadow-sm transition hover:shadow-md ${palette.card}`}
        >

            <div className="flex h-full items-center justify-between gap-3">

                <div className="min-w-0">

                    <p
                        className={`text-[10px] font-semibold ${palette.label}`}
                    >
                        {label}
                    </p>

                    {!showGauge && (

                        <p
                            className={`mt-2 text-xl font-bold ${palette.value}`}
                        >
                            {value}
                        </p>

                    )}

                    {detail && (

                        <p className="mt-2 text-[10px] leading-4 text-slate-500">
                            {detail}
                        </p>

                    )}

                </div>

                {showGauge && (

                    <EvalCircularScore
                        score={score}
                        size={72}
                        color={color}
                    />

                )}

            </div>

        </div>

    )

}


/*
|--------------------------------------------------------------------------
| 06 - Filter Chip
|--------------------------------------------------------------------------
*/

const CategoryChip = ({
    selected,
    onClick,
    children,
}) => (

    <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className={`rounded-full border px-3 py-1.5 text-[10px] font-semibold transition ${
            selected
                ? 'border-orange-600 bg-orange-600 text-white shadow-sm'
                : 'border-gray-200 bg-white text-gray-600 hover:border-orange-300 hover:bg-orange-50'
        }`}
    >
        {children}
    </button>

)


/*
|--------------------------------------------------------------------------
| 07 - Evaluation Chart Tooltip
|--------------------------------------------------------------------------
*/

const EvaluationTrendTooltip = ({
    active,
    payload,
}) => {

    if (!active || !payload?.length) {
        return null
    }

    const point = payload[0]?.payload

    if (!point) {
        return null
    }

    return (

        <div className="max-w-xs rounded border border-orange-200 bg-white p-3 font-poppins shadow-lg">

            <p className="text-[11px] font-bold text-gray-800">
                {formatEvaluationDateTime(point.evaluatedAt)}
            </p>

            <p className="mt-1 text-xs font-semibold text-orange-600">
                Score: {formatScore(point.score)} / 5
            </p>

            {point.dimensionCount !== undefined && (

                <p className="mt-1 text-[10px] text-gray-500">
                    {point.dimensionCount} scored dimensions
                </p>

            )}

            {point.dateSource === 'createdAt' && (

                <p className="mt-2 text-[10px] italic text-amber-700">
                    Recorded date used. Actual evaluation
                    date is not confirmed.
                </p>

            )}

            {point.evalNote && (

                <p className="mt-2 whitespace-pre-wrap text-[10px] text-gray-600">
                    {point.evalNote}
                </p>

            )}

        </div>

    )

}


/*
|--------------------------------------------------------------------------
| 08 - Evaluation Progress View
|--------------------------------------------------------------------------
|
| Latest Eval:
|   Latest scored observation for each dimension.
|
| Eval 1, Eval 2, Eval 3:
|   Chronological evaluation events.
|
| Category, Dimension and Latest Eval remain fixed
| while historical evaluations scroll horizontally.
|
|--------------------------------------------------------------------------
*/

const EvaluationProgressView = ({
    dimensionSeries = [],
    evaluationEvents = [],
    selectedCategoryId,
    onSelectCategory,
}) => {

    /*
    |--------------------------------------------------------------------------
    | Chronological Evaluation Events
    |--------------------------------------------------------------------------
    */

    const historicalEvents = useMemo(() => {

        return evaluationEvents
            .filter(
                event =>
                    event.dimensionEvals?.length > 0
            )
            .slice()
            .sort((a, b) => {

                const dateDifference =
                    new Date(a.evaluatedAt).getTime() -
                    new Date(b.evaluatedAt).getTime()

                return (
                    dateDifference ||
                    a.matrixId.localeCompare(b.matrixId)
                )

            })

    }, [evaluationEvents])


    /*
    |--------------------------------------------------------------------------
    | Historical Score Lookup
    |--------------------------------------------------------------------------
    |
    | matrixId -> dimensionId -> score
    |
    |--------------------------------------------------------------------------
    */

    const scoreByEvent = useMemo(() => {

        const map = new Map()

        historicalEvents.forEach(event => {

            const dimensions = new Map()

            event.dimensionEvals.forEach(item => {

                dimensions.set(
                    item.dimensionId,
                    item.score
                )

            })

            map.set(
                event.matrixId,
                dimensions
            )

        })

        return map

    }, [historicalEvents])


    /*
    |--------------------------------------------------------------------------
    | Category Filter
    |--------------------------------------------------------------------------
    */

    const filteredDimensions = useMemo(() => {

        return dimensionSeries.filter(series => {

            return (
                selectedCategoryId === 'all' ||
                (series.categoryId || '') ===
                    selectedCategoryId
            )

        })

    }, [
        dimensionSeries,
        selectedCategoryId,
    ])


    return (

        <div>

            {/* Progress Header */}

            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">

                <div>

                    <p className="text-xs font-bold text-gray-700">
                        Dimension Progress
                    </p>

                    <p className="mt-1 text-[10px] text-gray-500">
                        Latest scores alongside chronological
                        evaluation history.
                    </p>

                </div>

                {selectedCategoryId !== 'all' && (

                    <button
                        type="button"
                        onClick={() =>
                            onSelectCategory('all')
                        }
                        className="rounded-full border border-orange-200 px-3 py-1 text-[10px] font-semibold text-orange-600 hover:bg-orange-50"
                    >
                        Show All Categories
                    </button>

                )}

            </div>


            {/* Progress Matrix */}

            <div className="overflow-hidden rounded-lg border border-orange-100">

                <div className="max-h-[540px] overflow-auto">

                    <table className="w-full min-w-max border-separate border-spacing-0 text-left">

                        <thead className="sticky top-0 z-30 bg-orange-50">

                            <tr>

                                <th className="sticky left-0 z-40 w-40 min-w-40 border-b border-r border-orange-100 bg-orange-50 px-3 py-3 text-[10px] font-bold text-gray-700">
                                    Category
                                </th>

                                <th className="sticky left-40 z-40 w-52 min-w-52 border-b border-r border-orange-100 bg-orange-50 px-3 py-3 text-[10px] font-bold text-gray-700">
                                    Dimension
                                </th>

                                <th className="sticky left-[368px] z-40 w-40 min-w-40 border-b border-r-2 border-orange-200 bg-orange-100 px-3 py-3 text-[10px] font-bold text-orange-700">
                                    Latest Eval
                                </th>

                                {historicalEvents.map((event, index) => (

                                    <th
                                        key={event.matrixId}
                                        className="min-w-36 border-b border-r border-orange-100 bg-orange-50 px-3 py-2 text-[10px] font-bold text-gray-700"
                                        title={formatEvaluationDateTime(
                                            event.evaluatedAt
                                        )}
                                    >

                                        <p>
                                            Eval {index + 1}
                                        </p>

                                        <p className="mt-1 text-[9px] font-normal text-gray-500">
                                            {formatShortEvaluationDate(
                                                event.evaluatedAt
                                            )}
                                        </p>

                                        {event.dateSource === 'createdAt' && (

                                            <p className="text-[9px] text-amber-600">
                                                Recorded
                                            </p>

                                        )}

                                    </th>

                                ))}

                            </tr>

                        </thead>


                        <tbody>

                            {filteredDimensions.map(
                                (series, index) => {

                                    const latestPoint =
                                        series.points?.[
                                            series.points.length - 1
                                        ]

                                    const latestScore =
                                        latestPoint?.score ?? null

                                    const rowBackground =
                                        index % 2 === 0
                                            ? 'bg-white'
                                            : 'bg-sky-50/30'

                                    return (

                                        <tr
                                            key={`${series.coachId}-${series.dimensionId}`}
                                            className={rowBackground}
                                        >

                                            {/* Category */}

                                            <td
                                                className={`sticky left-0 z-20 border-b border-r border-gray-100 px-3 py-2 ${rowBackground}`}
                                            >

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        onSelectCategory(
                                                            selectedCategoryId ===
                                                                series.categoryId
                                                                ? 'all'
                                                                : (
                                                                    series.categoryId ||
                                                                    ''
                                                                )
                                                        )
                                                    }
                                                    title="Filter by category"
                                                    className="text-left text-[10px] font-semibold text-orange-600 hover:underline"
                                                >
                                                    {series.categoryName}
                                                </button>

                                            </td>


                                            {/* Dimension */}

                                            <td
                                                className={`sticky left-40 z-20 border-b border-r border-gray-100 px-3 py-2 ${rowBackground}`}
                                            >

                                                <p className="max-w-48 whitespace-normal text-[11px] font-semibold text-gray-700">
                                                    {series.dimensionName}
                                                </p>

                                            </td>


                                            {/* Latest Evaluation */}

                                            <td className="sticky left-[368px] z-20 border-b border-r-2 border-orange-200 bg-orange-50 px-3 py-2">

                                                <EvalScoreScale
                                                    score={latestScore}
                                                    compact
                                                    highlight
                                                />

                                                {latestPoint && (

                                                    <p className="mt-1 text-[9px] text-gray-500">
                                                        {formatShortEvaluationDate(
                                                            latestPoint.evaluatedAt
                                                        )}
                                                    </p>

                                                )}

                                            </td>


                                            {/* Historical Evaluations */}

                                            {historicalEvents.map(event => {

                                                const score =
                                                    scoreByEvent
                                                        .get(event.matrixId)
                                                        ?.get(series.dimensionId)

                                                const detail =
                                                    event.dimensionEvals.find(
                                                        item =>
                                                            item.dimensionId ===
                                                            series.dimensionId
                                                    )

                                                return (

                                                    <td
                                                        key={`${event.matrixId}-${series.dimensionId}`}
                                                        className="border-b border-r border-gray-100 px-3 py-2 align-middle"
                                                        title={
                                                            detail
                                                                ? `${formatEvaluationDateTime(event.evaluatedAt)} | Score: ${formatScore(score)} / 5${detail.evalNote ? ` | ${detail.evalNote}` : ''}`
                                                                : 'Not evaluated in this assessment'
                                                        }
                                                    >

                                                        <EvalScoreScale
                                                            score={score}
                                                            compact
                                                        />

                                                    </td>

                                                )

                                            })}

                                        </tr>

                                    )

                                }
                            )}


                            {filteredDimensions.length === 0 && (

                                <tr>

                                    <td
                                        colSpan={
                                            historicalEvents.length + 3
                                        }
                                        className="p-6 text-center text-xs italic text-gray-400"
                                    >
                                        No dimension evaluations available.
                                    </td>

                                </tr>

                            )}

                        </tbody>

                    </table>

                </div>

            </div>


            {/* Scale Legend */}

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">

                <div className="flex items-center gap-2">

                    <div
                        className="h-[5px] w-20 rounded-full"
                        style={{
                            background:
                                'linear-gradient(to right, #fda4af, #fcd34d, #bef264, #5eead4)',
                        }}
                    />

                    <p className="text-[10px] text-gray-500">
                        Evaluation scale: 1 to 5
                    </p>

                </div>

                <p className="text-[10px] italic text-gray-400">
                    Scroll horizontally for additional evaluations.
                </p>

            </div>

        </div>

    )

}


/*
|--------------------------------------------------------------------------
| 09 - Evaluation Charts View
|--------------------------------------------------------------------------
|
| Category chips select the category.
|
| Dimension chips select either:
|
| - Category Overview
| - Individual dimension
|
| X-axis uses the actual evaluation date.
|
|--------------------------------------------------------------------------
*/

const EvaluationChartsView = ({
    categorySeries = [],
    dimensionSeries = [],
}) => {

    const [
        selectedCategoryId,
        setSelectedCategoryId,
    ] = useState('')

    const [
        selectedDimensionId,
        setSelectedDimensionId,
    ] = useState('all')


    /*
    |--------------------------------------------------------------------------
    | Category And Dimension Selection
    |--------------------------------------------------------------------------
    */

    const chartCategories = useMemo(() => {

        return categorySeries
            .map(series => ({
                categoryId: series.categoryId || '',
                categoryName: series.categoryName,
            }))
            .sort(
                (a, b) =>
                    a.categoryName.localeCompare(
                        b.categoryName
                    )
            )

    }, [categorySeries])


    const activeCategoryId =
        chartCategories.some(
            category =>
                category.categoryId === selectedCategoryId
        )
            ? selectedCategoryId
            : chartCategories[0]?.categoryId || ''


    const activeCategoryDimensions = useMemo(() => {

        return dimensionSeries
            .filter(
                series =>
                    (series.categoryId || '') ===
                    activeCategoryId
            )
            .slice()
            .sort(
                (a, b) =>
                    a.dimensionName.localeCompare(
                        b.dimensionName
                    )
            )

    }, [
        dimensionSeries,
        activeCategoryId,
    ])


    const activeDimensionId =
        activeCategoryDimensions.some(
            series =>
                series.dimensionId === selectedDimensionId
        )
            ? selectedDimensionId
            : 'all'


    const selectedCategorySeries =
        categorySeries.find(
            series =>
                (series.categoryId || '') ===
                activeCategoryId
        ) || null


    const selectedDimensionSeries =
        activeCategoryDimensions.find(
            series =>
                series.dimensionId === activeDimensionId
        ) || null


    const selectedSeries =
        activeDimensionId === 'all'
            ? selectedCategorySeries
            : selectedDimensionSeries


    /*
    |--------------------------------------------------------------------------
    | Prepare Chart Data
    |--------------------------------------------------------------------------
    */

    const chartData = useMemo(() => {

        return (selectedSeries?.points || [])
            .map(point => ({

                ...point,

                evaluationTime:
                    new Date(
                        point.evaluatedAt
                    ).getTime(),

            }))
            .filter(
                point =>
                    Number.isFinite(
                        point.evaluationTime
                    )
            )
            .sort(
                (a, b) =>
                    a.evaluationTime -
                    b.evaluationTime ||
                    a.matrixId.localeCompare(
                        b.matrixId
                    )
            )

    }, [selectedSeries])


    const firstPoint =
        chartData[0] || null

    const latestPoint =
        chartData[chartData.length - 1] || null

    const movement =
        chartData.length >= 2
            ? Number(
                (
                    latestPoint.score -
                    firstPoint.score
                ).toFixed(2)
            )
            : null


    return (

        <div>

            {/* Charts Header */}

            <div className="mb-3">

                <p className="text-xs font-bold text-gray-700">
                    Evaluation Movement Charts
                </p>

                <p className="mt-1 text-[10px] text-gray-500">
                    Select a category and then a dimension
                    to explore progression over time.
                </p>

            </div>


            {/* Category Chips */}

            <div className="rounded-lg bg-violet-50/60 p-3">

                <p className="mb-2 text-[10px] font-bold text-violet-700">
                    Categories
                </p>

                <div className="flex flex-wrap gap-2">

                    {chartCategories.map(category => (

                        <CategoryChip
                            key={category.categoryId}
                            selected={
                                activeCategoryId ===
                                category.categoryId
                            }
                            onClick={() => {

                                setSelectedCategoryId(
                                    category.categoryId
                                )

                                setSelectedDimensionId('all')

                            }}
                        >
                            {category.categoryName}
                        </CategoryChip>

                    ))}

                </div>

            </div>


            {/* Dimension Chips */}

            {selectedCategorySeries && (

                <div className="mt-3 rounded-lg bg-sky-50/60 p-3">

                    <p className="mb-2 text-[10px] font-bold text-sky-700">
                        Dimensions
                    </p>

                    <div className="flex flex-wrap gap-2">

                        <CategoryChip
                            selected={
                                activeDimensionId === 'all'
                            }
                            onClick={() =>
                                setSelectedDimensionId('all')
                            }
                        >
                            Category Overview
                        </CategoryChip>

                        {activeCategoryDimensions.map(
                            series => (

                                <CategoryChip
                                    key={series.dimensionId}
                                    selected={
                                        activeDimensionId ===
                                        series.dimensionId
                                    }
                                    onClick={() =>
                                        setSelectedDimensionId(
                                            series.dimensionId
                                        )
                                    }
                                >
                                    {series.dimensionName}
                                </CategoryChip>

                            )
                        )}

                    </div>

                </div>

            )}


            {/* Selected Chart */}

            {selectedSeries ? (

                <div className="mt-4 rounded-lg border border-orange-100 bg-white p-3">

                    <div className="flex flex-wrap items-start justify-between gap-3">

                        <div>

                            <p className="text-xs font-bold text-gray-800">
                                {selectedDimensionSeries
                                    ? selectedDimensionSeries.dimensionName
                                    : selectedCategorySeries?.categoryName}
                            </p>

                            <p className="mt-1 text-[10px] text-gray-500">
                                {selectedDimensionSeries
                                    ? 'Dimension Score Movement'
                                    : 'Category Average Movement'}
                            </p>

                        </div>

                        <div className="text-right">

                            <p
                                className={`text-lg font-bold ${
                                    getMovementColor(movement)
                                }`}
                            >
                                {formatMovement(movement)}
                            </p>

                            <p className="text-[10px] text-gray-500">
                                First to latest
                            </p>

                        </div>

                    </div>


                    {/* Chart Summary */}

                    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">

                        <EvalMetricCard
                            label="First Evaluation"
                            color="sky"
                            score={firstPoint?.score}
                            showGauge
                            detail={
                                formatShortEvaluationDate(
                                    firstPoint?.evaluatedAt
                                )
                            }
                        />

                        <EvalMetricCard
                            label="Latest Evaluation"
                            color="orange"
                            score={latestPoint?.score}
                            showGauge
                            detail={
                                formatShortEvaluationDate(
                                    latestPoint?.evaluatedAt
                                )
                            }
                        />

                        <EvalMetricCard
                            label="Evaluations"
                            value={chartData.length}
                            color="purple"
                        />

                    </div>


                    {/* Trend Chart */}

                    <div className="mt-4 h-72 w-full">

                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                        >

                            <LineChart
                                data={chartData}
                                margin={{
                                    top: 10,
                                    right: 20,
                                    left: 0,
                                    bottom: 12,
                                }}
                            >

                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="#e5e7eb"
                                />

                                <XAxis
                                    dataKey="evaluationTime"
                                    type="number"
                                    scale="time"
                                    domain={
                                        chartData.length === 1
                                            ? [
                                                chartData[0].evaluationTime -
                                                    86400000,
                                                chartData[0].evaluationTime +
                                                    86400000,
                                            ]
                                            : [
                                                'dataMin',
                                                'dataMax',
                                            ]
                                    }
                                    tickFormatter={
                                        formatShortEvaluationDate
                                    }
                                    tick={{
                                        fontSize: 10,
                                    }}
                                    minTickGap={24}
                                />

                                <YAxis
                                    type="number"
                                    domain={[1, 5]}
                                    ticks={[1, 2, 3, 4, 5]}
                                    tick={{
                                        fontSize: 10,
                                    }}
                                />

                                <Tooltip
                                    content={
                                        <EvaluationTrendTooltip />
                                    }
                                />

                                <ReferenceLine
                                    y={3}
                                    stroke="#d1d5db"
                                    strokeDasharray="4 4"
                                />

                                <Line
                                    type="linear"
                                    dataKey="score"
                                    stroke="#ea580c"
                                    strokeWidth={3}
                                    dot={{
                                        r: 5,
                                        fill: '#ea580c',
                                        stroke: '#fff',
                                        strokeWidth: 2,
                                    }}
                                    activeDot={{
                                        r: 7,
                                        fill: '#0d9488',
                                        stroke: '#fff',
                                        strokeWidth: 2,
                                    }}
                                    isAnimationActive={false}
                                />

                            </LineChart>

                        </ResponsiveContainer>

                    </div>


                    <p className="mt-2 text-[10px] text-gray-500">
                        The horizontal axis represents actual
                        evaluation dates. Scores range from 1 to 5.
                    </p>


                    {/* Evaluation History */}

                    <div className="mt-4 border-t border-orange-100 pt-3">

                        <p className="mb-2 text-[11px] font-bold text-gray-700">
                            Evaluation History
                        </p>

                        <div className="max-h-56 overflow-y-auto">

                            {chartData.map(point => (

                                <div
                                    key={point.matrixId}
                                    className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 py-2 last:border-b-0"
                                >

                                    <div className="flex-1">

                                        <p className="text-[10px] font-semibold text-gray-700">
                                            {formatEvaluationDateTime(
                                                point.evaluatedAt
                                            )}
                                        </p>

                                        {point.dimensionCount !== undefined && (

                                            <p className="text-[9px] text-gray-400">
                                                {point.dimensionCount}
                                                {' '}scored dimensions
                                            </p>

                                        )}

                                        {point.evalNote && (

                                            <p className="mt-1 whitespace-pre-wrap text-[10px] text-gray-600">
                                                {point.evalNote}
                                            </p>

                                        )}

                                    </div>

                                    <EvalCircularScore
                                        score={point.score}
                                        size={48}
                                        color="teal"
                                        strokeWidth={8}
                                    />

                                </div>

                            ))}

                        </div>

                    </div>

                </div>

            ) : (

                <p className="mt-4 rounded border border-dashed border-gray-200 p-5 text-xs text-gray-500">
                    No evaluation chart is available.
                </p>

            )}

        </div>

    )

}


/*
|--------------------------------------------------------------------------
| 10 - Evaluation Stats View
|--------------------------------------------------------------------------
|
| Updated - 28 Oct 2026
|
| Distinct softly colored metric cards.
|
| Circular progress gauges for:
| - Overall latest score
| - Category scores
| - Dimension scores
| - Individual evaluation scores
|
|--------------------------------------------------------------------------
*/

const EvaluationStatsView = ({
    evaluationEvents = [],
    dimensionSeries = [],
    overview = {},
}) => {

    /*
    |--------------------------------------------------------------------------
    | 28 Oct 2026 - Selected Evaluation Tab
    |--------------------------------------------------------------------------
    |
    | Evaluation tabs follow the existing reverse
    | chronological ordering.
    |
    | Default to the most recent evaluation.
    |
    |--------------------------------------------------------------------------
    */

    const [
        selectedEvaluationId,
        setSelectedEvaluationId,
    ] = useState(null)


    /*
    |--------------------------------------------------------------------------
    | Latest Dimension Scores
    |--------------------------------------------------------------------------
    */

    const latestDimensionScores = useMemo(() => {

        return dimensionSeries
            .map(series => {

                const latestPoint =
                    series.points?.[
                        series.points.length - 1
                    ]

                return {

                    ...series,

                    latestScore:
                        latestPoint?.score ?? null,

                    latestEvaluatedAt:
                        latestPoint?.evaluatedAt || null,

                }

            })
            .filter(
                series =>
                    typeof series.latestScore === 'number'
            )

    }, [dimensionSeries])


    /*
    |--------------------------------------------------------------------------
    | Overall Latest Score
    |--------------------------------------------------------------------------
    |
    | Unweighted average of each dimension's latest score.
    |
    | It may combine scores from different evaluation dates.
    |
    |--------------------------------------------------------------------------
    */

    const overallLatestScore = useMemo(() => {

        if (latestDimensionScores.length === 0) {
            return null
        }

        const total = latestDimensionScores.reduce(
            (sum, series) =>
                sum + series.latestScore,
            0
        )

        return Number(
            (
                total /
                latestDimensionScores.length
            ).toFixed(2)
        )

    }, [latestDimensionScores])


    /*
    |--------------------------------------------------------------------------
    | Latest Category Scores
    |--------------------------------------------------------------------------
    */

    const latestCategoryStats = useMemo(() => {

        const grouped = new Map()

        latestDimensionScores.forEach(series => {

            const categoryKey =
                series.categoryId ||
                `uncategorized:${series.dimensionId}`

            if (!grouped.has(categoryKey)) {

                grouped.set(categoryKey, {

                    categoryId: series.categoryId,
                    categoryName: series.categoryName,
                    scores: [],

                })

            }

            grouped.get(categoryKey)
                .scores.push(
                    series.latestScore
                )

        })

        return [...grouped.values()]
            .map(category => ({

                ...category,

                dimensionCount:
                    category.scores.length,

                averageScore: Number(
                    (
                        category.scores.reduce(
                            (sum, score) =>
                                sum + score,
                            0
                        ) /
                        category.scores.length
                    ).toFixed(2)
                ),

            }))
            .sort(
                (a, b) =>
                    a.categoryName.localeCompare(
                        b.categoryName
                    )
            )

    }, [latestDimensionScores])


    /*
    |--------------------------------------------------------------------------
    | Recent Evaluation Events
    |--------------------------------------------------------------------------
    */

    const recentEvaluationEvents = useMemo(() => {

        return evaluationEvents
            .filter(
                event =>
                    event.dimensionEvals?.length > 0
            )
            .slice()
            .sort(
                (a, b) =>
                    new Date(b.evaluatedAt).getTime() -
                    new Date(a.evaluatedAt).getTime() ||
                    b.matrixId.localeCompare(a.matrixId)
            )

    }, [evaluationEvents])

    /*
    |--------------------------------------------------------------------------
    | 28 Oct 2026 - Active Evaluation For Tab Display
    |--------------------------------------------------------------------------
    |
    | If no evaluation has been selected, show the latest.
    | If selection is no longer available, fall back
    | to the latest evaluation.
    |
    |--------------------------------------------------------------------------
    */

    const activeEvaluationIndex =
        recentEvaluationEvents.findIndex(
            event => event.matrixId === selectedEvaluationId
        )

    const activeEvaluation =
        activeEvaluationIndex >= 0
            ? recentEvaluationEvents[activeEvaluationIndex]
            : recentEvaluationEvents[0] || null


    /*
    |--------------------------------------------------------------------------
    | Category Palette
    |--------------------------------------------------------------------------
    */

    const getCategoryColor = index => {

        return categoryColorNames[
            index % categoryColorNames.length
        ]

    }


    return (

        <div>

            {/*----------------------------------------------------------
              Stats Summary
            ----------------------------------------------------------*/}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">

                <EvalMetricCard
                    label="Evaluations"
                    value={overview.evaluationCount || 0}
                    detail="Total recorded evaluation events"
                    color="sky"
                />

                <EvalMetricCard
                    label="Overall Latest Score"
                    score={overallLatestScore}
                    showGauge
                    detail="Average of latest dimension scores"
                    color="orange"
                />

                <EvalMetricCard
                    label="Dimensions Evaluated"
                    value={overview.dimensionsEvaluated || 0}
                    detail="Distinct evaluated capabilities"
                    color="purple"
                />

                <EvalMetricCard
                    label="Categories Evaluated"
                    value={overview.categoriesEvaluated || 0}
                    detail="Evaluation areas covered"
                    color="teal"
                />

            </div>


            {/*----------------------------------------------------------
              Movement Summary
            ----------------------------------------------------------*/}

            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">

                <EvalMetricCard
                    label="Improving"
                    value={overview.improvingDimensions || 0}
                    detail="Dimensions with positive movement"
                    color="lime"
                />

                <EvalMetricCard
                    label="Stable"
                    value={overview.stableDimensions || 0}
                    detail="Dimensions with unchanged scores"
                    color="amber"
                />

                <EvalMetricCard
                    label="Declining"
                    value={overview.decliningDimensions || 0}
                    detail="Dimensions with negative movement"
                    color="rose"
                />

            </div>


            {/*----------------------------------------------------------
              Latest Scores By Category
            ----------------------------------------------------------*/}

            <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">

                <p className="text-xs font-bold text-gray-700">
                    Latest Scores by Category
                </p>

                <p className="mt-1 text-[10px] text-gray-500">
                    Each category score averages the latest
                    available scores of its evaluated dimensions.
                </p>


                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">

                    {latestCategoryStats.map(
                        (category, index) => {

                            const color =
                                getCategoryColor(index)

                            const palette =
                                metricColorStyles[color]

                            return (

                                <div
                                    key={
                                        category.categoryId ||
                                        category.categoryName
                                    }
                                    className={`rounded-lg border p-3 shadow-sm transition hover:shadow-md ${palette.card}`}
                                >

                                    <div className="flex items-center justify-between gap-3">

                                        <div className="min-w-0 flex-1">

                                            <p className={`text-[11px] font-bold ${palette.label}`}>
                                                {category.categoryName}
                                            </p>

                                            <p className="mt-2 text-[10px] text-slate-500">
                                                {category.dimensionCount}
                                                {' '}evaluated dimensions
                                            </p>

                                        </div>

                                        <EvalCircularScore
                                            score={
                                                category.averageScore
                                            }
                                            size={82}
                                            color={color}
                                        />

                                    </div>


                                    {/* Category Score Scale */}

                                    <div className="mt-3 rounded bg-white/70 p-2">

                                        <EvalScoreScale
                                            score={
                                                category.averageScore
                                            }
                                            highlight
                                        />

                                    </div>

                                </div>

                            )

                        }
                    )}

                </div>


                {latestCategoryStats.length === 0 && (

                    <p className="mt-3 text-xs italic text-gray-400">
                        No category scores are available.
                    </p>

                )}

            </div>


            {/*----------------------------------------------------------
              Dimension Score Details
            ----------------------------------------------------------*/}

            <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">

                <p className="text-xs font-bold text-gray-700">
                    Dimension Score Details
                </p>

                <p className="mt-1 text-[10px] text-gray-500">
                    Latest evaluated scores and first-to-latest
                    movement for each dimension.
                </p>


                <div className="mt-3 max-h-96 overflow-auto">

                    <table className="w-full min-w-[650px] text-left">

                        <thead className="sticky top-0 z-10 bg-orange-50">

                            <tr>

                                <th className="p-3 text-[10px] text-gray-600">
                                    Category
                                </th>

                                <th className="p-3 text-[10px] text-gray-600">
                                    Dimension
                                </th>

                                <th className="p-3 text-[10px] text-gray-600">
                                    Latest Score
                                </th>

                                <th className="p-3 text-[10px] text-gray-600">
                                    Movement
                                </th>

                                <th className="p-3 text-[10px] text-gray-600">
                                    Evaluations
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {dimensionSeries.map(
                                (series, index) => {

                                    const latestPoint =
                                        series.points?.[
                                            series.points.length - 1
                                        ]

                                    const movement =
                                        series.evaluationCount >= 2
                                            ? series.movement
                                            : null

                                    return (

                                        <tr
                                            key={`${series.coachId}-${series.dimensionId}`}
                                            className={`border-b border-gray-100 ${
                                                index % 2 === 0
                                                    ? 'bg-white'
                                                    : 'bg-sky-50/40'
                                            }`}
                                        >

                                            <td className="p-3 text-[10px] text-gray-500">
                                                {series.categoryName}
                                            </td>

                                            <td className="p-3 text-[11px] font-semibold text-gray-700">
                                                {series.dimensionName}
                                            </td>

                                            <td className="p-2">

                                                <EvalCircularScore
                                                    score={
                                                        latestPoint?.score
                                                    }
                                                    size={48}
                                                    color="orange"
                                                    strokeWidth={8}
                                                />

                                            </td>

                                            <td
                                                className={`p-3 text-xs font-bold ${
                                                    getMovementColor(
                                                        movement
                                                    )
                                                }`}
                                            >
                                                {formatMovement(
                                                    movement
                                                )}
                                            </td>

                                            <td className="p-3 text-[10px] text-gray-500">
                                                {series.evaluationCount}
                                            </td>

                                        </tr>

                                    )

                                }
                            )}

                        </tbody>

                    </table>

                </div>

            </div>


            
{/*----------------------------------------------------------
  Evaluation Scores And Coach Comments
  28 Oct 2026 - Tabbed Evaluation History
----------------------------------------------------------*/}

<div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">

    <p className="text-xs font-bold text-gray-700">
        Evaluation Scores &amp; Coach Comments
    </p>

    <p className="mt-1 text-[10px] text-gray-500">
        Select an evaluation to review its dimension scores
        and coach comments. Most recent evaluations appear first.
    </p>


    {/*------------------------------------------------------
      Evaluation Tabs
    ------------------------------------------------------*/}

    {recentEvaluationEvents.length > 0 && (

        <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-2">

            <div
                role="tablist"
                aria-label="Select coach evaluation"
                className="flex gap-2 overflow-x-auto whitespace-nowrap pb-1"
            >

                {recentEvaluationEvents.map((event, index) => {

                    const isSelected =
                        activeEvaluation?.matrixId === event.matrixId

                    return (

                        <button
                            key={event.matrixId}
                            type="button"
                            role="tab"
                            aria-selected={isSelected}
                            onClick={() =>
                                setSelectedEvaluationId(event.matrixId)
                            }
                            className={`shrink-0 rounded-lg border px-4 py-2 text-left transition ${
                                isSelected
                                    ? 'border-orange-500 bg-orange-600 text-white shadow-sm'
                                    : 'border-gray-200 bg-white text-gray-600 hover:border-orange-300 hover:bg-orange-50'
                            }`}
                        >

                            <p className="text-[11px] font-bold">
                                Evaluation {
                                    recentEvaluationEvents.length - index
                                }
                            </p>

                            <p
                                className={`mt-1 text-[9px] ${
                                    isSelected
                                        ? 'text-orange-100'
                                        : 'text-gray-400'
                                }`}
                            >
                                {formatShortEvaluationDate(
                                    event.evaluatedAt
                                )}
                            </p>

                        </button>

                    )

                })}

            </div>

        </div>

    )}


    {/*------------------------------------------------------
      Selected Evaluation Details
    ------------------------------------------------------*/}

    {activeEvaluation ? (

        (() => {

            const activeIndex =
                recentEvaluationEvents.findIndex(
                    event =>
                        event.matrixId ===
                        activeEvaluation.matrixId
                )

            const eventColor =
                categoryColorNames[
                    activeIndex % categoryColorNames.length
                ]

            const palette =
                metricColorStyles[eventColor]

            return (

                <div
                    key={activeEvaluation.matrixId}
                    className={`mt-3 rounded-lg border p-3 ${palette.card}`}
                >

                    {/* Evaluation Header */}

                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">

                        <p className={`text-[11px] font-bold ${palette.label}`}>
                            Evaluation {
                                recentEvaluationEvents.length -
                                activeIndex
                            }
                        </p>

                        <p className="rounded-full bg-white/80 px-3 py-1 text-[10px] font-semibold text-gray-700">
                            {formatEvaluationDateTime(
                                activeEvaluation.evaluatedAt
                            )}
                        </p>

                    </div>


                    {/* Evaluated Dimensions */}

                    <div className="space-y-2">

                        {activeEvaluation.dimensionEvals.map(item => (

                            <div
                                key={item.dimensionId}
                                className="rounded border border-white/80 bg-white/80 p-3"
                            >

                                <div className="flex flex-wrap items-center justify-between gap-3">

                                    <div className="min-w-0 flex-1">

                                        <p className="text-[9px] font-semibold text-gray-400">
                                            {item.categoryName}
                                        </p>

                                        <p className="mt-1 text-[11px] font-bold text-gray-700">
                                            {item.dimensionName}
                                        </p>

                                    </div>

                                    <EvalCircularScore
                                        score={item.score}
                                        size={54}
                                        color="orange"
                                        strokeWidth={8}
                                    />

                                </div>


                                {item.evalNote && (

                                    <div className="mt-2 rounded border-l-2 border-orange-300 bg-orange-50/70 p-2">

                                        <p className="text-[9px] font-bold text-orange-700">
                                            Coach Comments
                                        </p>

                                        <p className="mt-1 whitespace-pre-wrap text-[10px] leading-5 text-gray-600">
                                            {item.evalNote}
                                        </p>

                                    </div>

                                )}

                            </div>

                        ))}

                    </div>

                </div>

            )

        })()

    ) : (

        <p className="mt-3 text-xs italic text-gray-400">
            No scored evaluations are available.
        </p>

    )}

</div>


        </div>

    )

}


/*
|--------------------------------------------------------------------------
| 11 - Coach User Session Evaluation Trends
|--------------------------------------------------------------------------
|
| First Created - 27 Oct 2026
| Updated - 28 Oct 2026
|
| Three Views:
|
| 1. Progress
|    Category / dimension score matrix with chronology.
|
| 2. Charts
|    Category and dimension movement over evaluation dates.
|
| 3. Stats
|    Color-coded scorecards, circular gauges,
|    category and dimension statistics, coach comments.
|
|--------------------------------------------------------------------------
*/

const EtCoachUserSessionEvalTrends = ({

    coachId,
    userId,

    orgId,
    trailId,

    sessionId,
    cohortId,

}) => {

    /*
    |--------------------------------------------------------------------------
    | 12 - Main View State
    |--------------------------------------------------------------------------
    */

    const [
        activeView,
        setActiveView,
    ] = useState('progress')

    const [
        progressCategoryId,
        setProgressCategoryId,
    ] = useState('all')


    /*
    |--------------------------------------------------------------------------
    | 13 - Load Evaluation Categories
    |--------------------------------------------------------------------------
    */

    const {
        data: categoryData,
        isLoading: isCategoriesLoading,
        isError: isCategoriesError,
    } = useGetEvalCategoriesQuery({
        active: 'all',
    })


    /*
    |--------------------------------------------------------------------------
    | 14 - Load Evaluation Dimensions
    |--------------------------------------------------------------------------
    */

    const {
        data: dimensionData,
        isLoading: isDimensionsLoading,
        isError: isDimensionsError,
    } = useGetEvalDimensionsQuery({
        active: 'all',
    })


    /*
    |--------------------------------------------------------------------------
    | 15 - Load Evaluation Matrices
    |--------------------------------------------------------------------------
    */

    const matrixQueryArgs = useMemo(() => ({

        coachId,
        userId,
        orgId,
        trailId,

        ...(sessionId && {
            sessionId,
        }),

        ...(cohortId && {
            cohortId,
        }),

    }), [
        coachId,
        userId,
        orgId,
        trailId,
        sessionId,
        cohortId,
    ])


    const {
        data: matrixData,
        isLoading: isMatricesLoading,
        isFetching: isMatricesFetching,
        isError: isMatricesError,
        refetch: refetchMatrices,
    } = useGetCoachUserSessionEvalMatrixsQuery(
        matrixQueryArgs,
        {
            skip:
                !coachId ||
                !userId ||
                !orgId ||
                !trailId,
        }
    )


    /*
    |--------------------------------------------------------------------------
    | 16 - Build Chronological Evaluation Dataset
    |--------------------------------------------------------------------------
    */

    const trends = useMemo(() => {

        return buildCoachUserSessionEvalTrends({

            matrices: matrixData,
            categories: categoryData,
            dimensions: dimensionData,

            coachId,
            userId,
            orgId,
            trailId,
            sessionId,
            cohortId,

        })

    }, [
        matrixData,
        categoryData,
        dimensionData,
        coachId,
        userId,
        orgId,
        trailId,
        sessionId,
        cohortId,
    ])


    /*
    |--------------------------------------------------------------------------
    | 17 - Evaluated Category Filters
    |--------------------------------------------------------------------------
    */

    const evaluatedCategories = useMemo(() => {

        const map = new Map()

        trends.dimensionSeries.forEach(series => {

            const key =
                series.categoryId ||
                `uncategorized:${series.dimensionId}`

            if (!map.has(key)) {

                map.set(key, {

                    id: series.categoryId || '',
                    name: series.categoryName,

                })

            }

        })

        return [...map.values()]
            .sort(
                (a, b) =>
                    a.name.localeCompare(
                        b.name
                    )
            )

    }, [trends.dimensionSeries])


    /*
    |--------------------------------------------------------------------------
    | 18 - Loading And Error Handling
    |--------------------------------------------------------------------------
    */

    if (
        !coachId ||
        !userId ||
        !orgId ||
        !trailId
    ) {
        return (

            <p className="p-3 text-xs text-gray-500">
                Select a coach, participant, organization
                and trail to view evaluations.
            </p>

        )
    }

    if (
        isCategoriesLoading ||
        isDimensionsLoading ||
        isMatricesLoading
    ) {
        return (

            <div className="flex justify-center p-4">

                <PulseLoader
                    color="#ea580c"
                    size={7}
                />

            </div>

        )
    }

    if (
        isCategoriesError ||
        isDimensionsError ||
        isMatricesError
    ) {
        return (

            <p className="p-3 text-xs text-red-600">
                Unable to load coach evaluation data.
            </p>

        )
    }


    /*
    |--------------------------------------------------------------------------
    | 19 - Main Workspace
    |--------------------------------------------------------------------------
    */

    return (

        <div className="rounded-lg border border-orange-200 bg-white p-3 font-poppins">

            {/*----------------------------------------------------------
              Workspace Header
            ----------------------------------------------------------*/}

            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-orange-100 pb-3">

                <div>

                    <p className="text-sm font-bold text-orange-600">
                        Coach Session Evaluation Progress
                    </p>

                    <p className="mt-1 text-[10px] text-gray-500">
                        Participant capability progression
                        across coach evaluations.
                    </p>

                </div>

                <div className="flex items-center gap-2">

                    {isMatricesFetching && (

                        <span className="text-[10px] text-gray-400">
                            Refreshing...
                        </span>

                    )}

                    <button
                        type="button"
                        onClick={() =>
                            refetchMatrices()
                        }
                        className="rounded-full border border-orange-200 px-3 py-1 text-[10px] font-semibold text-orange-600 hover:bg-orange-50"
                    >
                        Refresh
                    </button>

                </div>

            </div>


            {/*----------------------------------------------------------
              Main View Tabs
            ----------------------------------------------------------*/}

            <div className="mt-3 border-b border-orange-100">

                <div
                    role="tablist"
                    aria-label="Evaluation progress views"
                    className="flex flex-wrap gap-2"
                >

                    {[
                        {
                            id: 'progress',
                            label: 'Progress',
                        },
                        {
                            id: 'charts',
                            label: 'Charts',
                        },
                        {
                            id: 'stats',
                            label: 'Stats',
                        },
                    ].map(view => {

                        const isSelected =
                            activeView === view.id

                        return (

                            <button
                                key={view.id}
                                type="button"
                                role="tab"
                                aria-selected={isSelected}
                                onClick={() =>
                                    setActiveView(view.id)
                                }
                                className={`rounded-t px-5 py-2 text-[11px] font-bold transition ${
                                    isSelected
                                        ? 'border-b-2 border-orange-600 bg-orange-50 text-orange-600'
                                        : 'text-gray-500 hover:bg-orange-50/40 hover:text-gray-700'
                                }`}
                            >
                                {view.label}
                            </button>

                        )

                    })}

                </div>

            </div>


            {/*----------------------------------------------------------
              Progress View
            ----------------------------------------------------------*/}

            {activeView === 'progress' && (

                <div className="mt-4">

                    {/* Category Filter Chips */}

                    <div className="mb-3 flex flex-wrap items-center gap-2">

                        <CategoryChip
                            selected={
                                progressCategoryId === 'all'
                            }
                            onClick={() =>
                                setProgressCategoryId('all')
                            }
                        >
                            All Categories
                        </CategoryChip>

                        {evaluatedCategories.map(category => (

                            <CategoryChip
                                key={
                                    category.id ||
                                    category.name
                                }
                                selected={
                                    progressCategoryId ===
                                    category.id
                                }
                                onClick={() =>
                                    setProgressCategoryId(
                                        progressCategoryId ===
                                            category.id
                                            ? 'all'
                                            : category.id
                                    )
                                }
                            >
                                {category.name}
                            </CategoryChip>

                        ))}

                    </div>


                    <EvaluationProgressView

                        dimensionSeries={
                            trends.dimensionSeries
                        }

                        evaluationEvents={
                            trends.evaluationEvents
                        }

                        selectedCategoryId={
                            progressCategoryId
                        }

                        onSelectCategory={
                            setProgressCategoryId
                        }

                    />

                </div>

            )}


            {/*----------------------------------------------------------
              Charts View
            ----------------------------------------------------------*/}

            {activeView === 'charts' && (

                <div className="mt-4">

                    <EvaluationChartsView

                        categorySeries={
                            trends.categorySeries
                        }

                        dimensionSeries={
                            trends.dimensionSeries
                        }

                    />

                </div>

            )}


            {/*----------------------------------------------------------
              Stats View
            ----------------------------------------------------------*/}

            {activeView === 'stats' && (

                <div className="mt-4">

                    <EvaluationStatsView

                        evaluationEvents={
                            trends.evaluationEvents
                        }

                        dimensionSeries={
                            trends.dimensionSeries
                        }

                        overview={
                            trends.overview
                        }

                    />

                </div>

            )}


            {/*----------------------------------------------------------
              Historical Evaluation Date Notice
            ----------------------------------------------------------*/}

            {trends.overview.dateFallbackCount > 0 && (

                <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[10px] text-amber-800">

                    {trends.overview.dateFallbackCount}
                    {' '}evaluation(s) are using
                    their recorded date because the actual
                    evaluation date has not yet been populated.

                    {' '}Dates can be corrected in the
                    individual evaluation editors.

                </p>

            )}

        </div>

    )

}


export default EtCoachUserSessionEvalTrends